import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import api from "@/services/api";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMyContext } from "@/context/ContextApi";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Signup = () => {
  const { t } = useTranslation();
  const [role] = useState("USER");
  const [loading, setLoading] = useState(false);
  const { token } = useMyContext();
  const navigate = useNavigate();

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm({
    defaultValues: { username: "", email: "", password: "" },
    mode: "onTouched",
  });

  const onSubmitHandler = async (data) => {
    const { username, email, password } = data;
    try {
      setLoading(true);
      const response = await api.post("/auth/public/signup", {
        username, email, password, role,
      });
      toast.success(t('signup_successMessage'));
      reset();
      if (response.data) navigate("/login");
    } catch (error) {
      if (error?.response?.data?.message === "Error: Username is already taken!") {
        setError("username", { message: t('signup_usernameAlreadyTaken') });
      } else if (error?.response?.data?.message === "Error: Email is already in use!") {
        setError("email", { message: t('signup_emailAlreadyInUse') });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) navigate("/");
  }, [navigate, token]);

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-white">{t('signup_title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('signup_subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-4">

          {/* Username */}
          <div className="space-y-1.5">
            <Label htmlFor="username" className="text-zinc-400 text-xs uppercase tracking-wider">{t('signup_usernameLabel')}</Label>
            <Input
              id="username"
              placeholder={t('signup_usernamePlaceholder')}
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
              {...register("username", { required: true})}
            />
            {errors.username && (
              <p className="text-xs text-red-400">{errors.username.message || t('signup_usernameRequired')}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-zinc-400 text-xs uppercase tracking-wider">{t('signup_emailLabel')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('signup_emailPlaceholder')}
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
              {...register("email", { required: true })}
            />
            {errors.email && (
              <p className="text-xs text-red-400">{errors.email.message || t('signup_emailRequired')}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-zinc-400 text-xs uppercase tracking-wider">{t('signup_passwordLabel')}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t('signup_passwordPlaceholder')}
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
              {...register("password", { required: true, minLength: 8 })}
            />
            {errors.password && (
              <p className="text-xs text-red-400">{t('signup_passwordMinLength')}</p>
            )}
          </div>

          <Button
            disabled={loading}
            type="submit"
            className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold"
          >
            {loading ? t('signup_submitButtonLoading') : t('signup_submitButton')}
          </Button>
        </form>

        <p className="mt-5 text-center text-xs text-zinc-600">
          {t('signup_signinPrompt')}{" "}
          <Link to="/login" className="text-zinc-400 hover:text-white transition-colors">
            {t('signup_signinLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
